import { CircularProgress, Grid } from '@mui/material';
import { Language } from '@nx-smartmonkey/shared/domain';
import { toDayNoon } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalDialog,
  RoutalInputDatePicker,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { PlanStatus } from '../domain/plan/PlanStatus';
import { ProjectPlans, defaultNameSubstitutions } from '../domain/project/ProjectPlans';
import { intl } from '../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../hooks/project/useRetrieveActiveProject';
import { useRetrieveUser } from '../hooks/user/useRetrieveUser';
import { EditableContent } from '../pages/project-settings/plans/EditableContent';
import Button from '../react-components/Button';
import InputText from '../react-components/InputText';
import Text from '../react-components/Text';

const MAX_DUPLICATE_PLANS = 30;

const WeekdayButton = ({
  weekday,
  selected,
  onClick,
}: {
  weekday: Weekday;
  selected: boolean;
  onClick: () => void;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: `pointer`,
        color: selected || hover ? `white` : `black`,
        backgroundColor: selected || hover ? RoutalPalette.secondary40 : `white`,
        border: `1px solid ${selected || hover ? `transparent` : RoutalPalette.neutral10}`,
        width: `40px`,
        height: `40px`,
        borderRadius: `50%`,
        display: `flex`,
        flexDirection: `row`,
        alignItems: `center`,
        justifyContent: `center`,
        margin: `0 8px`,
      }}
      onClick={onClick}
    >
      {weekday.name}
    </div>
  );
};

type DuplicatePlanDialogProps = {
  isLoading?: boolean;
  handleDuplicatePlan: (duplicates: { label: string; status: PlanStatus; execution_date: Date }[]) => void;
  duplicateDialogOpen: boolean;
  handleOpenDuplicateDialog: (...args: any[]) => any;
};

interface Weekday {
  name: string;
  index: number;
}

const getWeekdays = (locale: string): Weekday[] => {
  const weekdays = [];
  const momentLocale = moment.localeData(locale);
  const firstDayOfWeek = momentLocale.firstDayOfWeek();

  let index = 0;
  for (let i = firstDayOfWeek; i < firstDayOfWeek + 7; i++) {
    const dayName = momentLocale.weekdaysMin()[i % 7];
    weekdays.push({ name: dayName, index });
    index++;
  }

  return weekdays;
};

const periodicityOptions = [`no_repeat`, `daily`, `weekly`];

export const DuplicatePlanDialog = ({
  isLoading = false,
  handleDuplicatePlan,
  handleOpenDuplicateDialog,
  duplicateDialogOpen,
}: DuplicatePlanDialogProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();
  const [newPlanLabel, setNewPlanLabel] = useState<string>(``);
  const [startDate, setStartDate] = useState<Date>(toDayNoon(new Date()));
  const [endDate, setEndDate] = useState<Date>(toDayNoon(moment(new Date()).add(1, `day`).toDate()));
  const [newPlanPeriodicity, setNewPlanPeriodicity] = useState<string>(periodicityOptions[0]);
  const [newPlanPeriodicityValue, setNewPlanPeriodicityValue] = useState<number>(1);
  const [newPlanWeeklyDays, setNewPlanWeeklyDays] = useState<
    [boolean, boolean, boolean, boolean, boolean, boolean, boolean]
  >([false, false, false, false, false, false, false]);
  const [duplicates, setDuplicates] = useState<{ label: string; status: PlanStatus; execution_date: Date }[]>([]);

  useEffect(() => {
    if (activeProject) {
      setNewPlanLabel(activeProject.plans.label);
      const defaultStartDate = activeProject.plans.defaultStartDateToValue();
      setStartDate(defaultStartDate);
      setEndDate(moment(defaultStartDate).add(1, `day`).toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeProject) {
      const newDuplicates: { label: string; status: PlanStatus; execution_date: Date }[] = [];

      let executionDate = moment(startDate).locale(user?.language.value ?? Language.getDefaultLanguage().value);

      while (newDuplicates.length < MAX_DUPLICATE_PLANS && executionDate.toDate().getTime() < endDate.getTime()) {
        if (newPlanPeriodicity === `weekly`) {
          const weekExecutionDate = moment(executionDate);

          weekExecutionDate.weekday(0);

          // eslint-disable-next-line no-loop-func
          newPlanWeeklyDays.forEach((enabled, index) => {
            if (enabled && newDuplicates.length < MAX_DUPLICATE_PLANS) {
              const weekdayDate = moment(weekExecutionDate).weekday(index).toDate();

              if (weekdayDate.getTime() <= endDate.getTime() && weekdayDate.getTime() >= startDate.getTime()) {
                newDuplicates.push({
                  label: ProjectPlans.labelTemplateToValue({
                    template: newPlanLabel,
                    date: weekdayDate,
                    language: user?.language.value,
                  }),
                  status: activeProject.plans.status,
                  execution_date: weekdayDate,
                });
              }
            }
          });
          executionDate.add(newPlanPeriodicityValue, `week`);
        } else {
          newDuplicates.push({
            label: ProjectPlans.labelTemplateToValue({
              template: newPlanLabel,
              date: executionDate.toDate(),
              language: user?.language.value,
            }),
            status: activeProject.plans.status,
            execution_date: executionDate.toDate(),
          });

          if (newPlanPeriodicity === `daily`) {
            executionDate.add(newPlanPeriodicityValue, `day`);
          } else {
            executionDate = moment(endDate);
          }
        }
      }

      setDuplicates(newDuplicates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, newPlanLabel, newPlanPeriodicity, newPlanPeriodicityValue, newPlanWeeklyDays, startDate]);

  if (!activeProject || !duplicateDialogOpen) return null;

  return (
    <RoutalDialog
      id="duplicate-plan-dialog"
      onCloseDialog={handleOpenDuplicateDialog}
      open={duplicateDialogOpen}
      titleContent={<Text variant="h6" intlId="duplicate_plan_dialog.title" />}
      titleRightContent={
        <Button
          disabled={isLoading}
          style={{
            minWidth: `125px`,
            height: `20px`,
          }}
          onClick={() => {
            handleDuplicatePlan(duplicates);
          }}
        >
          {isLoading ? (
            <CircularProgress size={32} />
          ) : (
            <Text variant="button" intlId="duplicate_plan_dialog.button_create" style={{ color: `white` }} />
          )}
        </Button>
      }
      showFooter={false}
    >
      <Text variant="label" intlId="duplicate_plan_dialog.label" />

      <VSpacer small />

      <EditableContent
        id="new-plan-label-editable-content"
        value={newPlanLabel}
        substitutions={defaultNameSubstitutions.map((substitution) => ({
          id: substitution,
          label: intl.formatMessage({
            id: `projects.settings.plans.label.substitutions.${substitution}`,
          }),
        }))}
        placeholder={intl.formatMessage({ id: `plan_settings_dialog.plan_label` })}
        onValueChange={(value: string) => {
          setNewPlanLabel(value);
        }}
      />

      <VSpacer large />

      <Grid container direction="row" columnGap={1} alignItems="space-between" wrap="nowrap">
        <Grid item xs={4}>
          <Grid container direction="column">
            <RoutalInputDatePicker
              label={intl.formatMessage({ id: `duplicate_plan_dialog.start_date` })}
              dateValue={startDate}
              onChange={(date: Date) => {
                setStartDate(toDayNoon(date));
                if (date.getTime() >= endDate.getTime()) {
                  setEndDate(toDayNoon(moment(date).add(1, `day`).toDate()));
                }
              }}
              locale={user?.language.value}
              portalId="duplicate-plan-dialog"
            />

            {newPlanPeriodicity !== `no_repeat` ? (
              <>
                <VSpacer medium />

                <RoutalInputDatePicker
                  label={intl.formatMessage({ id: `duplicate_plan_dialog.end_date` })}
                  dateValue={endDate}
                  minDate={startDate}
                  onChange={(date: Date) => {
                    setEndDate(toDayNoon(date));
                  }}
                  locale={user?.language.value}
                  portalId="duplicate-plan-dialog"
                />
              </>
            ) : null}
          </Grid>
        </Grid>

        <Grid item xs={8}>
          <Grid container direction="row" wrap="nowrap" alignItems="center">
            <Grid item xs={6}>
              <Grid container direction="column">
                <Text variant="label" intlId="duplicate_plan_dialog.periodicity" style={{ marginBottom: `4px` }} />

                <RoutalSelect
                  id="select-periodicity"
                  containerStyle={{
                    marginTop: `4px`,
                  }}
                  popoverProps={{
                    options: periodicityOptions.map(
                      (periodicity): RoutalSelectMultiSelectOption => ({
                        key: periodicity,
                        content: (
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            alignItems="center"
                            style={{ overflow: `hidden` }}
                          >
                            <Text
                              tagName="span"
                              style={{
                                textAlign: `justify`,
                                whiteSpace: `nowrap`,
                                overflow: `hidden`,
                                width: `100%`,
                                textOverflow: `ellipsis`,
                                cursor: `pointer`,
                              }}
                              intlId={`duplicate_plan_dialog.periodicity.${periodicity}`}
                            />
                          </Grid>
                        ),
                        onClick: () => {
                          if (periodicity !== `weekly`) {
                            setNewPlanWeeklyDays([false, false, false, false, false, false, false]);
                          }
                          setNewPlanPeriodicityValue(1);
                          setNewPlanPeriodicity(periodicity);
                        },
                      })
                    ),
                  }}
                >
                  <Text
                    style={{
                      cursor: `pointer`,
                      width: `100%`,
                      textAlign: `justify`,
                    }}
                    intlId={`duplicate_plan_dialog.periodicity.${newPlanPeriodicity}`}
                  />
                </RoutalSelect>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              {newPlanPeriodicity === `no_repeat` ? null : (
                <Grid container direction="row" alignItems="center" style={{ marginTop: `16px` }} wrap="nowrap">
                  <HSpacer medium />

                  <Grid item>
                    <Text style={{ textTransform: `lowercase` }} intlId="duplicate_plan_dialog.periodicity.every" />
                  </Grid>

                  <HSpacer small />

                  <Grid item>
                    <InputText
                      type="number"
                      inputContainerStyles={{
                        width: `60px`,
                      }}
                      inputProps={{
                        min: 1,
                      }}
                      fieldValue={newPlanPeriodicityValue}
                      onChange={(value: any) => {
                        setNewPlanPeriodicityValue(value);
                      }}
                    />
                  </Grid>

                  <HSpacer small />

                  <Grid item>
                    <Text
                      style={{ textTransform: `lowercase` }}
                      intlId={newPlanPeriodicity === `weekly` ? `weeks` : `days`}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {newPlanPeriodicity !== `no_repeat` ? (
        <>
          <VSpacer large />

          {newPlanPeriodicity === `weekly` ? (
            <>
              <Text variant="label" intlId="duplicate_plan_dialog.periodicity.week_days" />

              <VSpacer medium />

              <Grid container direction="row" wrap="nowrap" alignItems="center">
                {getWeekdays(user?.language.value ?? Language.getDefaultLanguage().value).map((weekDay) => (
                  <Grid item key={weekDay.name}>
                    <WeekdayButton
                      weekday={weekDay}
                      selected={newPlanWeeklyDays[weekDay.index]}
                      onClick={() => {
                        newPlanWeeklyDays[weekDay.index] = !newPlanWeeklyDays[weekDay.index];
                        setNewPlanWeeklyDays([...newPlanWeeklyDays]);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>

              <VSpacer medium />
            </>
          ) : null}

          <Text variant="label" intlId="duplicate_plan_dialog.periodicity.result_info" />

          <VSpacer small />

          <Text
            intlId="duplicate_plan_dialog.periodicity.result"
            intlValues={{
              value: newPlanPeriodicityValue,
              unit: intl.formatMessage({
                id: newPlanPeriodicity === `daily` ? `days` : `weeks`,
              }),
              startDate: startDate.toLocaleDateString(user?.language.value),
              endDate: endDate.toLocaleDateString(user?.language.value),
            }}
          />

          {duplicates.length === MAX_DUPLICATE_PLANS ? (
            <Grid container direction="column" alignItems="flex-end">
              <VSpacer medium />

              <Text variant="label" style={{ fontSize: `12px`, textTransform: `lowercase` }}>
                {`*${intl.formatMessage({ id: `duplicate_plan_dialog.max_duplicates` })}`}
              </Text>
            </Grid>
          ) : (
            <VSpacer large />
          )}
        </>
      ) : (
        <VSpacer small />
      )}
    </RoutalDialog>
  );
};
